// A floating panel under its button, kept inside the window at any width (web/controls.js placePopover). It fades in;
// `leaving` fades it out, then onGone.
import { useLayoutEffect, useRef, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";
import styles from "./Popover.module.css";

interface Props {
  anchor: RefObject<HTMLElement | null>;
  id: string;
  className?: string;
  leaving?: boolean;
  onGone?: () => void;
  role?: string;
  label?: string;
  children: ReactNode;
  onPointerDown?: (ev: React.PointerEvent<HTMLDivElement>) => void;
}

export function Popover({ anchor, id, className, leaving, onGone, role = "dialog", label, children, onPointerDown }: Props) {
  const el = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const pop = el.current, a = anchor.current;
    if (!pop || !a) return;
    const place = () => {
      const r = a.getBoundingClientRect(), w = Math.min(pop.offsetWidth, innerWidth - 16);
      pop.style.left = `${Math.max(8, Math.min(r.left, innerWidth - w - 8))}px`;
      pop.style.top = `${r.bottom + 8}px`;
      pop.style.maxHeight = `${Math.max(160, innerHeight - r.bottom - 24)}px`;
    };
    place();
  }, [anchor]);
  return createPortal(
    <div ref={el} id={id} role={role} aria-label={label} onPointerDown={onPointerDown}
      className={[styles.popover, className, leaving && styles.leaving].filter(Boolean).join(" ")}
      onAnimationEnd={() => { if (leaving) onGone?.(); }}>
      {children}
    </div>,
    document.body,
  );
}
